import { useEffect, useState } from 'react';
import { Form, Input, Button, Card, message, Spin, Alert } from 'antd';
import { UserOutlined, LockOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { login, checkNewUser, sendCode, verifyCode, changePassword, checkUser } from "../services/auth.js";
import loginBg from '../assets/login_bg.png';
import logo from '../assets/logo.png';
import { config } from '../config/index.js';
import { useRef } from 'react';
import './styles/login.css';
const { API_BASE_URL } = config;

const Login = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [isChecking, setIsChecking] = useState(true);
  const [mode, setMode] = useState('login');
  const [countdown, setCountdown] = useState(0);
  const [username, setUsername] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('Low');
  const [passwordStrengthColor, setPasswordStrengthColor] = useState('red');
  const [passwordValue, setPasswordValue] = useState('');
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const calculatePasswordStrength = (password) => {
    setPasswordValue(password);
    if (!password) {
      setPasswordStrength('Low');
      setPasswordStrengthColor('red');
      return;
    }
    let strength = 0;
    if (/[a-zA-Z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) strength += 1;
    console.log('password strength:', strength);
    if (strength <= 1) {
      setPasswordStrength('Low');
      setPasswordStrengthColor('red');
    } else if (strength === 2) {
      setPasswordStrength('Medium');
      setPasswordStrengthColor('orange');
    } else {
      setPasswordStrength('Strong');
      setPasswordStrengthColor('green');
    }
  };
  useEffect(() => {
    setPasswordValue('');
    setPasswordStrength('Low');
    setPasswordStrengthColor('red');
  }, [mode]);

  useEffect(() => {
    const currentHostname = window.location.hostname;

    if (
      currentHostname === 'admin-dev.scams.report' ||
      currentHostname === 'admin.scams.report' 
      ||  currentHostname === 'localhost'
    ) {
      window.location.href = `${API_BASE_URL}/saml2/authenticate/aws-sso`;
      return;
    }

    const checkLoginStatus = () => {
      const token = localStorage.getItem('company_token');
      const expiresAt = localStorage.getItem('company_token_expires_at');

      if (!token || !expiresAt) {
        return;
      }

      if (Date.now() > Number(expiresAt)) {
        localStorage.removeItem('company_token');
        localStorage.removeItem('company_token_expires_at');
        return;
      }

      navigate('/home');
    };

    checkLoginStatus();
    setIsChecking(false);
  }, []);


  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const onLogin = async (values) => {
    setLoading(true);
    setLoginError(null);
    try {
      const response = await checkNewUser(username, values.password)
      if (response.code === 200) {
        if (response.login === 'false') {
          setLoginError('Invalid password.');
        } else {
          if (response.isNewUser === "true") {
            setMode('forceChange');
            return;
          } else {
            const res = await login(username, values.password);
            if (res.code === 200) {
              message.success('Login successful');
              navigate('/home');
            } else {
              setLoginError('Invalid password.');
            }
          }
        }
      } else {
        setLoginError('Invalid password.');
      }
    } catch {
      setLoginError('Invalid password.');
    } finally {
      setLoading(false);
    }
  };

  const checkUserHandler = async (username) => {
    setLoading(true);
    setLoginError(null);
    try {
      const response = await checkUser(username);
      if (response.code === 200 && response.isUser === "true") {
        setUsername(username);
        setMode('password');
      } else {
        setLoginError('Invalid email');
      }
    } catch (e) {
      setLoginError('Invalid email');
    } finally {
      setLoading(false);
    }
  };

  const sendEmailCode = async () => {
    try {
      const formValues = formRef.current?.getFieldsValue();
      const username = formValues?.username;

      if (!username) {
        message.warning('Please enter your email first');
        return;
      }

      const response = await sendCode(username);
      if (response.code === 200) {
        message.success('Verification code sent');
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown(c => {
            if (c <= 1) {
              clearInterval(timer);
              return 0;
            }
            return c - 1;
          });
        }, 1000);
      } else {
        message.error(response.msg || 'Verification code send failed');
      }
    } catch (error) {
      console.error('Failed to send verification code:', error);
      message.error('Verification code send failed');
    }
  };

  const onResetPassword = async (values) => {
    setLoading(true);
    if (countdown === 0) {
      message.error('Code expired. Please get a new code.');
      return;
    }
    if (passwordStrength === 'Low') {
      message.error('Password is too weak, please use at least 10 characters including letters, numbers and special characters.');
      return;
    }
    try {
      const response = await verifyCode(values.username, values.code, values.password);
      if (response.code === 200) {
        message.success('Password reset successfully');
        setMode('login');
      } else {
        message.error(response.data || 'Invalid verification code.');
      }
    } catch {
      message.error('Password reset failed');
    }
    setLoading(false);
  };
  const [submitting, setSubmitting] = useState(false);
  const onForceChange = async (values) => {
    if (submitting) return;
    if (passwordStrength === 'Low') {
      message.error('Password is too weak, please use at least 10 characters including letters, numbers and special characters.');
      return;
    }
    try {
      setSubmitting(true);
      const response = await changePassword(values.password, username);
      if (response.code === 200) {
        message.success('Password updated');
        navigate('/home');
      } else {
        message.error(response.msg || 'Password update failed');
      }
    } catch {
      message.error('Password update failed');
    } finally {
      setSubmitting(false);
      setUsername('');
    }
  };

  const renderCard = (title, subtitle, content) => (
    <Card className="login-card">
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h1>{title}</h1>
        <p style={{ color: '#888' }}>{subtitle}</p>
      </div>
      {content}
    </Card>
  );

  const PasswordStrengthTooltip = () => {
    if (!passwordValue) return null;

    const maxLength = 10;

    const lengthRatio = Math.min(passwordValue.length / maxLength, 1);
    const progressPercent = Math.round(lengthRatio * 100);

    let ruleScore = 0;
    if (/[a-zA-Z]/.test(passwordValue)) ruleScore += 1;
    if (/\d/.test(passwordValue)) ruleScore += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(passwordValue)) ruleScore += 1;

    let passwordStrength = '';
    let passwordStrengthColor = '';

    if (ruleScore <= 1) {
      passwordStrength = 'Low';
      passwordStrengthColor = '#f56c6c';
    } else if (ruleScore === 2) {
      passwordStrength = 'Medium';
      passwordStrengthColor = '#e6a23c';
    } else {
      passwordStrength = 'High';
      passwordStrengthColor = '#67c23a';
    }

    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '105%',
          width: 220,
          padding: '12px 14px',
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          zIndex: 10
        }}
      >
        <div style={{ marginBottom: 6, fontWeight: 500 }}>
          Password Strength:
          <span style={{ marginLeft: 6, color: passwordStrengthColor }}>
            {passwordStrength}
          </span>
        </div>

        <div
          style={{
            width: '100%',
            height: 6,
            background: '#eee',
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            marginBottom: 10
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${progressPercent}%`,
              background: passwordStrengthColor,
              borderRadius: 3,
              transition: 'width 0.25s ease-out'
            }}
          />
        </div>

        <div style={{ fontSize: 18, color: '#666', lineHeight: '18px' }}>
          Use at least 10 characters,<br />
          including letters, numbers<br />
          and special characters.
        </div>
      </div>
    );
  };

  return (
    <div className="login-container" style={{ backgroundImage: `url(${loginBg})` }}>
      <div className="login-header">
        <img src={logo} alt="logo" className="login-logo" />
        <span className="login-title">
          Scams Report Enterprise
        </span>
      </div>

      {isChecking ? <Spin size="large" /> : (
        <>
          {mode === 'login' && renderCard(
            'Scams Report Enterprise',
            'Admin System Login',
            <>
              {loginError && (
                <Alert
                  message={loginError}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setLoginError(null)}
                  style={{ marginBottom: 24, textAlign: 'left' }}
                />
              )}
              <Form onFinish={(values) => checkUserHandler(values.username)} size="large" form={form}>
                <Form.Item
                  name="username"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Email"
                    className="round-input"
                    onChange={() => { if (loginError) setLoginError(null); }}
                  />
                </Form.Item>
                <Button type="primary" htmlType="submit" block loading={loading} className="round-input">Continue</Button>
              </Form>
            </>
          )}

          {mode === 'password' && renderCard(
            'Enter Password',
            '',
            <>
              {loginError && (
                <Alert
                  message={loginError}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setLoginError(null)}
                  style={{ marginBottom: 24, textAlign: 'left' }}
                />
              )}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <UserOutlined style={{ fontSize: 48, color: '#999' }} />
                <div style={{ marginTop: 12 }}>{username}</div>
              </div>

              <Form size="large" onFinish={onLogin} form={passwordForm}>
                <Form.Item
                  name="password"
                  rules={[{ required: true, message: 'Please enter your password' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Password"
                    className="round-input"
                    onChange={() => { if (loginError) setLoginError(null); }}
                  />
                </Form.Item>
                <div style={{ textAlign: 'left', marginBottom: 12 }}>
                  <Button type="link" onClick={() => setMode('forgot')} style={{ padding: 0, height: 'auto' }}>
                    Forgot password
                  </Button>
                </div>
                <Button className="round-input" type="primary" htmlType="submit" block loading={loading}>
                  Login
                </Button>
              </Form>
            </>
          )}

          {mode === 'forgot' && renderCard(
            'Reset Password',
            'Verify username and set new password',
            <Form onFinish={onResetPassword} size="large" ref={formRef}>
              <Form.Item name="username" rules={[{ required: true, message: 'Please enter your email' }]}>
                <Input prefix={<UserOutlined />} placeholder="Email" className="round-input" />
              </Form.Item>
              <Form.Item name="code" rules={[{ required: true, message: 'Please enter verification code' }]}>
                <Input
                  className="round-input"
                  placeholder="Verification Code"
                  style={{ paddingRight: 0 }}
                  suffix={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fafafa',
                        height: '38px',
                        borderLeft: '1px solid #d9d9d9',
                        borderTopRightRadius: '50px',
                        borderBottomRightRadius: '50px',
                        padding: '0 20px',
                        marginRight: '-1px'
                      }}
                    >
                      <Button
                        type="text"
                        size="small"
                        disabled={countdown > 0}
                        onClick={sendEmailCode}
                        style={{
                          padding: 0,
                          height: 'auto',
                          lineHeight: 'normal'
                        }}
                      >
                        {countdown ? `${countdown}s` : 'Send Code'}
                      </Button>
                    </div>
                  }
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: 'Please enter password' },
                  { min: 10, message: 'Use at least 10 characters, including letters, numbers and special characters.' },
                  { max: 15, message: 'Password must not exceed 15 characters.' },
                ]}

                validateStatus={
                  passwordValue && (
                    passwordValue.length < 10 || passwordValue.length > 15
                  )
                    ? 'error'
                    : ''
                }
                help={
                  passwordValue && passwordValue.length < 10
                    ? 'Use at least 10 characters, including letters, numbers and special characters.'
                    : passwordValue && passwordValue.length > 15
                      ? 'Password must not exceed 15 characters.'
                      : ''
                }
              >
                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                  <Input.Password
                    placeholder="New Password"
                    className="round-input"
                    style={{ flex: 1 }}
                    onChange={(e) => calculatePasswordStrength(e.target.value)}
                  />
                  <PasswordStrengthTooltip />
                </div>
              </Form.Item>
              <Form.Item
                name="confirm"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, v) {
                      if (!v) return Promise.resolve();
                      return v === getFieldValue('password')
                        ? Promise.resolve()
                        : Promise.reject('Passwords do not match');
                    }
                  })
                ]}
              >
                <Input.Password placeholder="Confirm Password" className="round-input" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block className="round-input">Reset</Button>
              <Button type="link" block onClick={() => setMode('login')}>
                Back to login
              </Button>
            </Form>
          )}

          {mode === 'forceChange' && renderCard(
            'Change Password',
            'First login requires password update',
            <Form onFinish={onForceChange} size="large">

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: 'Please enter password' },
                  { min: 10, message: 'Use at least 10 characters, including letters, numbers and special characters.' },
                  { max: 15, message: 'Password must not exceed 15 characters.' },
                ]}
                validateStatus={
                  passwordValue && (
                    passwordValue.length < 10 || passwordValue.length > 15
                  )
                    ? 'error'
                    : ''
                }
                help={
                  passwordValue && passwordValue.length < 10
                    ? 'Use at least 10 characters, including letters, numbers and special characters.'
                    : passwordValue && passwordValue.length > 15
                      ? 'Password must not exceed 15 characters.'
                      : ''
                }
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Input.Password
                    placeholder="New Password"
                    className="round-input"
                    onChange={(e) => calculatePasswordStrength(e.target.value)}
                  />
                  <PasswordStrengthTooltip />
                </div>
              </Form.Item>
              <Form.Item
                name="confirm"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, v) {
                      if (!v) return Promise.resolve();
                      return v === getFieldValue('password')
                        ? Promise.resolve()
                        : Promise.reject('Passwords do not match');
                    }
                  })
                ]}
              >
                <Input.Password placeholder="Confirm Password" className="round-input" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block className="round-input">Update</Button>
            </Form>
          )}

          <div className="login-welcome-container">
            <img src={logo} alt="logo" className="login-welcome-logo" />
            <span className="login-welcome-text">
              Welcome to <span style={{ color: '#1677ff' }}>Scams Report Enterprise</span>
            </span>
            <span className="login-text">
              Let's keep your digital space secure today.
            </span>
          </div>
        </>
      )}
      <div className="login-footer">
        © All rights reserved | Cyberoo Pty Ltd
      </div>
    </div>
  );
};

export default Login;
